
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  description: 'Enhance discovery and conversion on bank payments via UX design'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Increasing pay-by-bank adoption' subtitle='Enhance discovery and conversion on bank payments via UX design' mdxType="Header" />
    <h4 {...{
      "id": "increasing-pay-by-bank-adoption"
    }}>{`Increasing Pay by bank adoption`}</h4>
    <p>{`Properly presenting  'pay by bank' as a payment method and showcasing the convenience of using Plaid for account authentication can result in up to 2-5x more users choosing to pay by bank.`}</p>
    <h4 {...{
      "id": "payment-method-presentation"
    }}>{`Payment method presentation`}</h4>
    <p>{`Place 'Pay by bank' as the first option in a payment list, have it pre-selected with a radio button, and display `}<a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search"
      }}>{`Embedded Institution Search`}</a>{` inline. This shows that instant verification is available and easy to use. `}</p>
    <Image src='/assets/img/docs/embedded-search-desktop.png' alt="Payment page with options for monthly, quarterly, or annual plans. 'Pay by bank' is selected. Bank logos displayed for selection. Total: $47.49/month." caption='Example of Embedded Institution Search on Desktop' expandable={true} mdxType="Image" />
    <h5 {...{
      "id": "embedded-institution-select-default-open"
    }}>{`Embedded Institution Select default open`}</h5>
    <p>{`Users are more likely to select 'Pay by bank' if they understand that it will be a secure, open banking-powered experience, rather than having to enter an account and routing number, which is their default expectation. Users are even more likely to use pay by bank if they see their bank's logo within Link.`}</p>
    <p>{`Plaid will dynamically show the user the most popular institutions for your application in their area, or you can customize this list via the Plaid Dashboard.`}</p>
    <h6 {...{
      "id": "recommendations"
    }}>{`Recommendations:`}</h6>
    <ul>
      <li parentName="ul">{`Show as many logos as possible, following the `}<a parentName="li" {...{
          "href": "/docs/link/embedded-institution-search/#customization-options"
        }}>{`breakpoint guidance`}</a>{`, in order to increase the likelihood that the user will see their bank. `}</li>
      <li parentName="ul">{`By providing the user’s phone number, you can activate the streamlined version of `}<a parentName="li" {...{
          "href": "/docs/link/returning-user/#pre-filling-phone-numbers-for-faster-account-verification"
        }}>{`returning user experience`}</a>{`, which has been shown to drive a 2x increase in bank payments adoption for returning users. `}</li>
      <li parentName="ul">{`Ensure Embedded Institution Search is visible by default, without requiring the user to first select 'Pay by bank'. `}</li>
    </ul>
    <h4 {...{
      "id": "using-accordion-open"
    }}>{`Using accordion open`}</h4>
    <p>{`If the Embedded Institution Search inline display option is not available to you due to design or technical constraints, use an "accordion open" technique. Once the user selects 'Pay by bank', render the Embedded Institution Search for easy user comprehension of their next steps to add a bank account. `}</p>
    <Image src='/assets/img/docs/embedded-link-accordion.gif' alt="Payment page with options for monthly, quarterly, annual plans. Select pay by bank or card. Pet insurance $47.49/month. Pay button." caption='Example of Embedded Institution Search on Desktop' expandable={true} mdxType="Image" />
    <h6 {...{
      "id": "recommendations-1"
    }}>{`Recommendations:`}</h6>
    <ul>
      <li parentName="ul">{`Labeling the payment method as 'Pay by bank' and adding an appropriate CTA such as 'instantly verify your bank account' will help users understand that your pay by bank flow is powered by open banking. `}</li>
      <li parentName="ul">{`Use "accordion open" if necessary to display Embedded Institution Search.`}</li>
    </ul>
    <h4 {...{
      "id": "configure-account-select"
    }}>{`Configure Account Select`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "/docs/link/customization/#account-select"
      }}>{`Account Select`}</a>{` option "Enabled for one account" setting configures the Link UI so that the end user may only select a single account. This is the appropriate configuration for most pay-by-bank use cases. You can configure this option in the Dashboard, under `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/account-select"
      }}>{`Link > Link Customization > Account Select`}</a>{`.`}</p>
    <h4 {...{
      "id": "enable-database-auth"
    }}>{`Enable Database Auth`}</h4>
    <p>{`Although most users prefer signing into their bank account via open banking, some prefer to provide their account and routing numbers manually. `}</p>
    <h5 {...{
      "id": "recommendations-2"
    }}>{`Recommendations:`}</h5>
    <ul>
      <li parentName="ul">{`To support both populations, enable `}<a parentName="li" {...{
          "href": "/docs/auth/coverage/database-auth/"
        }}>{`Database Auth`}</a>{`. `}</li>
    </ul>
    <Callout warning mdxType="Callout">
Database Auth is appropriate for low-to-medium-risk scenarios, such as recurring bill payments and loan repayments. If you experience high rates of fraud or ACH returns in your payments flow, you should not enable Database Auth.
    </Callout>
    <h4 {...{
      "id": "auth-type-select"
    }}>{`Auth Type Select`}</h4>
    <p>{`For user populations with a higher propensity to link accounts manually, Plaid provides `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/flow-options/#adding-manual-verification-entry-points-with-auth-type-select"
      }}>{`Auth Type Select`}</a>{` as an upfront option to the end user to choose between open banking login or manual account connection. Otherwise, this option will only be displayed if the user can't find their bank or encounters an error trying to connect.`}</p>
    <p>{`Examples of populations that may prefer manually linking include business users (who may not have access to their organization's online banking credentials) or users who are less comfortable with technology.`}</p>
    <Image src='/assets/img/docs/embedded-link-ats.gif' alt="Pay by bank: Select payment method with bank search and Auth Type Select. Monthly plan, $47.49/mo summary visible." caption='Embedded Institution Search with Auth Type Select enabled' expandable={true} mdxType="Image" />
    <Callout warning mdxType="Callout">
      <p>{`Auth Type Select will increase the percentage of users whose proposed payments cannot be evaluated for risk. Use `}<a parentName="p" {...{
          "href": "/docs/signal/signal-rules/#data-availability-limitations"
        }}>{`Signal`}</a>{` and `}<a parentName="p" {...{
          "href": "/docs/identity/#using-identity-match-with-micro-deposit-or-database-items"
        }}>{`Identity Match`}</a>{` with manually verified Items to reduce the risk of return. If you experience high rates of fraud or ACH returns in your payments flow, you should not enable Auth Type Select.`}</p>
    </Callout>
    <h5 {...{
      "id": "recommendations-3"
    }}>{`Recommendations:`}</h5>
    <ul>
      <li parentName="ul">{`Enable Auth Type Select if you have reason to believe that your user population prefers to link accounts manually and your risk profile can tolerate lower coverage of anti-fraud and anti-ACH-return checks.`}</li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "increasing-pay-by-bank-adoption",
        level: 4,
        title: "Increasing Pay by bank adoption",
      },
{
        id: "payment-method-presentation",
        level: 4,
        title: "Payment method presentation",
      },
{
        id: "using-accordion-open",
        level: 4,
        title: "Using accordion open",
      },
{
        id: "configure-account-select",
        level: 4,
        title: "Configure Account Select",
      },
{
        id: "enable-database-auth",
        level: 4,
        title: "Enable Database Auth",
      },
{
        id: "auth-type-select",
        level: 4,
        title: "Auth Type Select",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
